import { BookOpen, Boxes, ShieldCheck, Handshake, Building2, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, Container, SectionHead, FeatureGrid, CTASection, Eyebrow } from "@/components/common/Section";
import { Reveal } from "@/lib/anim";

const VALUES = [
  {
    icon: <BookOpen className="h-5 w-5" />,
    title: "Domain depth",
    desc: "Payments and banking expertise informs every architectural and product decision.",
  },
  {
    icon: <Boxes className="h-5 w-5" />,
    title: "Platforms, not projects",
    desc: "We build technology designed to be operated, extended and evolved for years.",
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "Security first",
    desc: "Security and operational controls are designed into the architecture from day one.",
  },
  {
    icon: <Handshake className="h-5 w-5" />,
    title: "Long-term partnership",
    desc: "We stay alongside clients from strategy and implementation through continuous evolution.",
  },
];

const CREDENTIALS = [
  "Nokia Money",
  "NPCI / UPI",
  "Core Banking System integrations",
  "Digital banking",
  "Financial inclusion",
];

export default function Company() {
  return (
    <>
      <PageHero
        eyebrow="About iJeeva"
        title="Experience that understands payments."
        copy="Founded in 2015, iJeeva is a Bengaluru-based technology company specializing in enterprise payment platforms, gateways and financial technology solutions."
        testid="company-page-title"
      >
        <Reveal delay={140} className="mt-9 flex flex-wrap gap-2.5">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-[13px] font-medium text-[#334155]">
            <Building2 className="h-3.5 w-3.5 text-[#2563EB]" aria-hidden="true" />
            Founded 2015
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-[13px] font-medium text-[#334155]">
            <MapPin className="h-3.5 w-3.5 text-[#2563EB]" aria-hidden="true" />
            Bengaluru, India
          </span>
        </Reveal>
      </PageHero>

      <section className="py-20 lg:py-24">
        <Container className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <Eyebrow>Who We Are</Eyebrow>
            <h2 className="mt-4 text-[30px] font-semibold leading-[1.15] tracking-tight text-[#1D305B] sm:text-4xl">
              Deep payments expertise, combined with hands-on engineering.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg">
              Our strength comes from combining deep payments expertise with hands-on engineering. We work closely with
              businesses, fintechs and financial institutions to design, build and evolve technology that powers
              real-world financial experiences.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">What we work on</p>
              <ul className="mt-5 space-y-3 text-[15px] leading-relaxed text-[#475569]">
                <li>Enterprise payment platforms and gateways</li>
                <li>Wallet and issuer infrastructure</li>
                <li>Banking integrations and automation</li>
                <li>Financial APIs and connected ecosystems</li>
              </ul>
            </div>
          </Reveal>
        </Container>
      </section>

      <section id="leadership" className="scroll-mt-24 border-y border-[#E2E8F0] bg-white py-20 lg:py-24">
        <Container className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <Eyebrow>Leadership</Eyebrow>
            <h2 className="mt-4 text-[30px] font-semibold leading-[1.15] tracking-tight text-[#1D305B] sm:text-4xl">
              Built by people who have been at the forefront of banking and payments.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg">
              Our founding team brings extensive experience across banking, payments and regulatory compliance, with
              hands-on experience in building and scaling financial technology platforms.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">Founding team experience</p>
              <ul className="mt-6 divide-y divide-[#E2E8F0]">
                {CREDENTIALS.map((c) => (
                  <li key={c} className="flex items-center gap-3.5 py-4">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0EA5A4]" aria-hidden="true" />
                    <span className="text-[15px] font-medium text-[#1D305B]">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHead eyebrow="Values" title="How we work." />
          <div className="mt-14">
            <FeatureGrid items={VALUES} cols={4} testidPrefix="company-value" />
          </div>
        </Container>
      </section>

      <section id="careers" className="scroll-mt-24 border-t border-[#E2E8F0] bg-white">
        <Container className="py-20 lg:py-24">
          <Reveal className="flex flex-col items-start justify-between gap-8 rounded-2xl bg-[#1D305B] p-8 sm:p-12 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <Eyebrow dark>Careers</Eyebrow>
              <h2 className="mt-4 text-[28px] font-semibold leading-tight tracking-tight text-white sm:text-3xl">
                Build the infrastructure behind modern payments.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[#94A3B8]">
                We work on payment platforms, banking integrations and financial infrastructure used in real production
                environments. If that sounds like your kind of engineering, we'd like to hear from you.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="careers-cta"
              className="group inline-flex shrink-0 items-center gap-2.5 rounded-[10px] bg-white px-6 py-3.5 text-[15px] font-semibold text-[#1D305B] transition-all duration-300 hover:-translate-y-0.5"
            >
              Get in touch
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </Container>
      </section>

      <CTASection
        title="Want to work with iJeeva?"
        copy="Whether you're building, modernizing or integrating, and our team is ready to talk."
      />
    </>
  );
}
